import { oc } from "@orpc/contract";
import { z } from "zod";

// ============================================
// Get User Permissions Contract
// ============================================

export const GetUserPermissionsInputSchema = z.object({
  userId: z.string().uuid(),
});

const PermissionSchema = z.object({
  action: z.string(),
  subject: z.string(),
});

const GroupWithPermissionsSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable(),
  permissions: z.array(PermissionSchema),
});

export const GetUserPermissionsOutputSchema = z.object({
  userId: z.string().uuid(),
  groups: z.array(GroupWithPermissionsSchema),
});

export const getUserPermissionsContract = oc
  .input(GetUserPermissionsInputSchema)
  .output(GetUserPermissionsOutputSchema)
  .route({ method: "GET" });

// ============================================
// Delete Group Contract
// ============================================

export const DeleteGroupInputSchema = z.object({
  groupId: z.string().uuid(),
});

export const DeleteGroupOutputSchema = z.object({
  success: z.literal(true),
});

export const deleteGroupContract = oc
  .input(DeleteGroupInputSchema)
  .output(DeleteGroupOutputSchema)
  .route({ method: "POST" });

// ============================================
// List Groups Contract — compact data for picker UIs
// ============================================

export const UserGroupBasicInfoSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable(),
});

export const ListUserGroupsOutputSchema = z.array(UserGroupBasicInfoSchema);

export const listUserGroupsContract = oc
  .output(ListUserGroupsOutputSchema)
  .route({ method: "GET" });

// ============================================
// Organization Groups contracts namespace
// ============================================

export const organizationGroupsContracts = {
  getUserPermissions: getUserPermissionsContract,
  deleteGroup: deleteGroupContract,
  list: listUserGroupsContract,
} as const;

// Export types
export type GetUserPermissionsInput = z.infer<
  typeof GetUserPermissionsInputSchema
>;
export type GetUserPermissionsOutput = z.infer<
  typeof GetUserPermissionsOutputSchema
>;
export type DeleteGroupInput = z.infer<typeof DeleteGroupInputSchema>;
export type DeleteGroupOutput = z.infer<typeof DeleteGroupOutputSchema>;
export type UserGroupBasicInfo = z.infer<typeof UserGroupBasicInfoSchema>;
export type ListUserGroupsOutput = z.infer<typeof ListUserGroupsOutputSchema>;
